import { useState } from "react";

import { Box, Flex, Image } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { TaskCheckHeader } from "flows/Inventory/shared/components/TaskCheckHeader";
import { useGetProductStockQuery } from "flows/Inventory/shared/queries/inventoryEntry/inventoryEntry.generated";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { transformChunks } from "shared/components/TextWrapper";
import { Button } from "ui/Button/Button";
import { IncreaseDecrease } from "ui/IncreaseDecrease/IncreaseDecrease";
import { BodyS, HeaderM, TitleS } from "ui/Typography/Typography";
import { formatImage } from "utils/image";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { useFreshCheckMutation } from "../hooks/useFreshCheckMutation";
import { useFreshCheckService } from "../hooks/useFreshCheckService";

const IMAGE_SIZE = 218;
export function FreshCheckTask() {
  const intl = useIntl();
  const context = useFreshCheckService();
  const {
    origin,
    shelfLetter,
    shelfNumber,
    sku,
    productName,
    productImageUrl,
    productCountryOfOriginName,
    loading: serviceLoading,
    isInvalidParams,
  } = context;

  const { declareStock, isUpdating } = useFreshCheckMutation({ context });

  const [quantityExpired, setQuantityExpired] = useState<number>(0);
  const [quantityDamaged, setQuantityDamaged] = useState<number>(0);
  const [quantityTooGoodToGo, setQuantityTooGoodToGo] = useState<number>(0);

  const { data: upToDateStockQuantity, loading } = useGetProductStockQuery({
    variables: { sku: sku!, isAlwaysInbound: true },
    fetchPolicy: "network-only",
    skip: !sku,
  });

  const totalStock = upToDateStockQuantity?.getProduct?.inventoryEntry.stock?.shelf ?? 0;
  const totalNewStock =
    (totalStock || 0) -
    ((quantityExpired || 0) + (quantityDamaged || 0) + (quantityTooGoodToGo || 0));

  const isIncreaseBtnDisabled = totalNewStock === 0;

  const handleDeclareStock = () => {
    declareStock({
      expectedQuantity: totalStock ?? 0,
      quantityDamaged,
      quantityExpired,
      quantityTooGoodToGo,
    });
    setQuantityExpired(0);
    setQuantityDamaged(0);
    setQuantityTooGoodToGo(0);
  };

  const imageUrlFormatted = isNotNullNorUndefined(productImageUrl)
    ? formatImage(productImageUrl, IMAGE_SIZE, IMAGE_SIZE)
    : null;

  if (serviceLoading || isInvalidParams) {
    return <SpinnerModal isOpen />;
  }

  return (
    <Page isCentered isBgGrey isFull>
      <SpinnerModal isOpen={loading} />
      <TaskCheckHeader
        headerMessageId="pages.fresh-check-header.fresh-check"
        origin={origin}
        shelfLetter={shelfLetter}
        shelfNumber={shelfNumber}
      />
      <Flex flex={1} flexDir="column" overflowY="scroll" w="100%">
        <Flex
          flex={1}
          align="center"
          flexDir="column"
          justify="center"
          px="s200"
          py="s100"
          gap="s100"
        >
          <Flex flex={1} align="center">
            {isNotNullNorUndefined(imageUrlFormatted) && (
              <Image
                src={imageUrlFormatted}
                objectFit="contain"
                h="25vh"
                maxW={`${IMAGE_SIZE}px`}
                maxH={`${IMAGE_SIZE}px`}
              />
            )}
          </Flex>
          <Flex px="s200" gap="s100">
            <Flex
              justifyContent="center"
              alignItems="center"
              px="s200"
              py="s100"
              bg="white"
              borderRadius="sm"
              alignSelf="stretch"
              w="100%"
              gap="s100"
            >
              <BodyS textAlign="center" color="grey.800" data-testid="stock-check-product-name">
                {productName}
                {productCountryOfOriginName ? ` (${productCountryOfOriginName})` : null}
              </BodyS>
            </Flex>
            <Flex
              direction="column"
              justifyContent="center"
              alignItems="center"
              p="s100"
              bg="white"
              borderRadius="sm"
            >
              <TitleS color="grey.800">
                {intl.formatMessage({
                  id: "generic.labels.stock-label",
                })}
              </TitleS>
              <HeaderM data-testid="product-stock">{totalStock}</HeaderM>
            </Flex>
          </Flex>
        </Flex>
        <Box bg="white">
          <Box
            overflowY="scroll"
            p="s150"
            m="s150"
            mb="0px"
            maxH="245px"
            css={{
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                width: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#DEDEDE",
                borderRadius: "8px",
              },
            }}
          >
            <IncreaseDecrease
              mb="s200"
              label={
                <FormattedMessage
                  id="components.increasedecrease.how_many_are_perished"
                  values={transformChunks({ b: { as: "strong" } })}
                />
              }
              changeHandler={setQuantityExpired}
              count={quantityExpired || 0}
              isIncreaseBtnDisabled={isIncreaseBtnDisabled}
            />
            <IncreaseDecrease
              mb="s200"
              label={
                <FormattedMessage
                  id="components.increasedecrease.how_many_are_damaged"
                  values={transformChunks({ b: { as: "strong" } })}
                />
              }
              changeHandler={setQuantityDamaged}
              count={quantityDamaged || 0}
              isIncreaseBtnDisabled={isIncreaseBtnDisabled}
            />
            <IncreaseDecrease
              dataTestId="too_good_to_go"
              label={
                <FormattedMessage
                  id="components.increasedecrease.too_good_to_go"
                  values={transformChunks({ b: { as: "strong" } })}
                />
              }
              changeHandler={setQuantityTooGoodToGo}
              isIncreaseBtnDisabled={isIncreaseBtnDisabled}
              count={quantityTooGoodToGo}
            />
          </Box>
          <Box p="s200" borderTopColor="grey.200" borderTopWidth="1px">
            <Button
              size="lg"
              isFullWidth
              onClick={handleDeclareStock}
              disabled={isUpdating}
              isLoading={isUpdating}
            >
              {intl.formatMessage({ id: "components.fresh-check.confirm-amount-button" })}
            </Button>
          </Box>
        </Box>
      </Flex>
    </Page>
  );
}
